import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  FileSpreadsheet, RefreshCw, Play, Terminal, CheckCircle2,
  AlertTriangle, XCircle, Clock, History, Loader2, Wallet,
  ChevronDown, ChevronRight, PlusCircle, Copy
} from 'lucide-react';
import { toast } from 'sonner';

// ── Fechas ───────────────────────────────────────────────────────────────────
// El backend habla dd/mm/aaaa (es el formato del filtro de ARCA); los <input
// type="date"> hablan aaaa-mm-dd. Se traduce en el borde.
const aInput = (ddmmaaaa) => {
  if (!ddmmaaaa) return '';
  const [d, m, a] = ddmmaaaa.split('/');
  return `${a}-${m}-${d}`;
};
const aBackend = (aaaammdd) => {
  if (!aaaammdd) return '';
  const [a, m, d] = aaaammdd.split('-');
  return `${d}/${m}/${a}`;
};
const fmtFechaHora = (iso) => {
  if (!iso) return '—';
  const f = new Date(iso);
  return `${f.toLocaleDateString('es-AR')} ${f.toLocaleTimeString('es-AR', {
    hour: '2-digit', minute: '2-digit'
  })}`;
};
const fmtNum = (v) =>
  v == null ? '—' : v.toLocaleString('es-AR', { minimumFractionDigits: 2 });

// ── Badges ───────────────────────────────────────────────────────────────────
// Mismo criterio de color en toda la herramienta: verde = cerrado/coincide,
// ámbar = hay algo para mirar, rojo = falta o no coincide, gris = desconocido.
const TONOS = {
  emerald: 'bg-emerald-950/40 border-emerald-800/60 text-emerald-300',
  amber:   'bg-amber-950/40 border-amber-800/60 text-amber-300',
  red:     'bg-red-950/40 border-red-800/60 text-red-300',
  sky:     'bg-sky-950/40 border-sky-800/60 text-sky-300',
  slate:   'bg-gray-800/50 border-gray-700 text-gray-300',
};

// Se mapea por ESTA_CODIGO cuando se lo conoce y, si no, por el nombre: así un
// estado nuevo de YiQi entra igual sin romper nada (queda gris).
const TONO_POR_CODIGO = { 98: 'emerald', 75: 'emerald', 1: 'amber', 72: 'red' };

const tonoEstado = (codigo, nombre = '') => {
  if (TONO_POR_CODIGO[codigo]) return TONO_POR_CODIGO[codigo];
  const n = nombre.toLowerCase();
  if (n.includes('pagad') || n.includes('emitid')) return 'emerald';
  if (n.includes('recibid') || n.includes('conformad')) return 'amber';
  if (n.includes('proyectad') || n.includes('sin comprobante') || n.includes('sin factura')
      || n.includes('anulad')) return 'red';
  return 'slate';
};

const tonoResultado = (resultado = '') => {
  const r = String(resultado).trim().toLowerCase();
  if (r === 'ok') return 'emerald';
  if (r.startsWith('creada') || r.startsWith('creado')) return 'sky';
  if (r.startsWith('diferente')) return 'amber';
  if (r.startsWith('no existe')) return 'red';
  return 'slate';
};

const Badge = ({ tono = 'slate', children, sufijo }) => (
  <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${TONOS[tono] || TONOS.slate}`}>
    {children}
    {sufijo != null && <span className="opacity-50 font-normal">#{sufijo}</span>}
  </span>
);

// Filtro por checkboxes. Cada opción muestra su cantidad y su color, así el
// filtro también funciona como resumen.
const FiltroChecks = ({ titulo, opciones, seleccion, onToggle, onTodos }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{titulo}</span>
    {opciones.map((o) => {
      const activo = seleccion.includes(o.clave);
      return (
        <label
          key={o.clave}
          className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 cursor-pointer select-none transition-colors ${
            activo ? TONOS[o.tono] || TONOS.slate : 'bg-transparent border-gray-800 text-gray-500 hover:border-gray-700'
          }`}
        >
          <input
            type="checkbox"
            checked={activo}
            onChange={() => onToggle(o.clave)}
            className="w-3 h-3 accent-cyan-500 cursor-pointer"
          />
          <span className="text-[11px] font-bold">{o.etiqueta}</span>
          <span className="text-[11px] opacity-60">{o.cantidad}</span>
        </label>
      );
    })}
    <button
      onClick={onTodos}
      className="text-[11px] font-bold text-gray-500 hover:text-white transition-colors px-1"
    >
      {seleccion.length === opciones.length ? 'Ninguno' : 'Todos'}
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Detalle de UNA conciliación.
//
// Lo usan tanto la corrida en vivo como cada fila desplegada del histórico: una
// sola implementación, así las dos vistas no pueden divergir. Recibe el mismo
// `resultado` que devuelven /estado/{job_id} y /historico/{job_id}.
// ─────────────────────────────────────────────────────────────────────────────
const DetalleConciliacion = ({
  resultado, logs = [], error, placeholderLog, alturaLog = 'h-80',
}) => {
  const [filtroEstados, setFiltroEstados] = useState([]);
  const [filtroResultados, setFiltroResultados] = useState([]);
  // Los duplicados arrancan plegados: son ruido hasta que los vas a resolver.
  const [duplicadosAbiertos, setDuplicadosAbiertos] = useState(false);
  const logRef = useRef(null);

  const resumen = resultado?.yiqi?.resumen;
  const descarga = resultado?.descarga;
  const verif = resultado?.yiqi?.verificacion;
  const comprobantes = verif?.comprobantes || [];

  const opcionesEstado = (verif?.por_estado || []).map((g) => ({
    clave: String(g.esta_codigo ?? 'sin_factura'),
    etiqueta: g.estado,
    cantidad: g.cantidad,
    tono: tonoEstado(g.esta_codigo, g.estado),
  }));
  const opcionesResultado = Object.entries(
    comprobantes.reduce((acc, c) => {
      const clave = tonoResultado(c.resultado_arca);
      acc[clave] = (acc[clave] || 0) + 1;
      return acc;
    }, {})
  ).map(([tono, cantidad]) => ({
    clave: tono,
    etiqueta: {
      emerald: 'Ok', sky: 'Creadas', amber: 'Con diferencia',
      red: 'No existe', slate: 'Otro',
    }[tono],
    cantidad,
    tono,
  }));

  // Al cambiar el detalle, todos los checks arrancan tildados.
  useEffect(() => {
    if (!verif) return;
    setFiltroEstados((verif.por_estado || []).map((g) => String(g.esta_codigo ?? 'sin_factura')));
    setFiltroResultados([...new Set((verif.comprobantes || []).map((c) => tonoResultado(c.resultado_arca)))]);
  }, [verif]);

  // Auto-scroll del log (importa en la corrida en vivo).
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const alternar = (lista, setLista) => (clave) =>
    setLista(lista.includes(clave) ? lista.filter((x) => x !== clave) : [...lista, clave]);

  const comprobantesFiltrados = comprobantes.filter(
    (c) =>
      filtroEstados.includes(String(c.esta_codigo ?? 'sin_factura')) &&
      filtroResultados.includes(tonoResultado(c.resultado_arca))
  );

  return (
    <div className="space-y-6">
      {/* ── Resumen de la importación ─────────────────────────────────────── */}
      {resumen && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Comprobantes</p>
            <p className="text-2xl font-black text-white mt-1">{resumen.total}</p>
            {descarga?.id_consulta && (
              <p className="text-[11px] text-gray-500 mt-1">consulta ARCA {descarga.id_consulta}</p>
            )}
          </div>
          <div className="bg-[#1A1D27] rounded-xl border border-emerald-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Ok
            </p>
            <p className="text-2xl font-black text-emerald-400 mt-1">{resumen.ok}</p>
          </div>
          <div className="bg-[#1A1D27] rounded-xl border border-sky-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest text-sky-600 font-bold flex items-center gap-1.5">
              <PlusCircle className="w-3 h-3" /> Creadas
            </p>
            <p className="text-2xl font-black text-sky-400 mt-1">{resumen.creada ?? 0}</p>
          </div>
          <div className="bg-[#1A1D27] rounded-xl border border-amber-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Con diferencia
            </p>
            <p className="text-2xl font-black text-amber-400 mt-1">{resumen.diferencia}</p>
          </div>
          <div className="bg-[#1A1D27] rounded-xl border border-red-900/40 p-4">
            <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold flex items-center gap-1.5">
              <XCircle className="w-3 h-3" /> No existen
            </p>
            <p className="text-2xl font-black text-red-400 mt-1">{resumen.no_existe}</p>
          </div>
        </div>
      )}

      {resumen?.incompleto && (
        <div className="bg-amber-950/30 border border-amber-900/50 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-400">El detalle quedó incompleto</p>
            <p className="text-xs text-amber-300/80 mt-0.5">
              ARCA mandó {resumen.esperadas} comprobantes y YiQi devolvió {resumen.total} filas
              de detalle. La importación se hizo igual: revisala en YiQi.
            </p>
          </div>
        </div>
      )}

      {/* ── Verificación de estados en YiQi ───────────────────────────────── */}
      {verif && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Wallet className="w-4 h-4 text-cyan-500" />
            <h2 className="text-sm font-black text-white">Estado de las facturas en YiQi</h2>
            <span className="text-xs text-gray-500">
              {verif.facturas_encontradas} encontrados · {verif.pagadas} facturas pagadas ·{' '}
              <span className="text-amber-400 font-bold">{verif.no_pagadas} sin pagar</span>
              {verif.otros_tipos > 0 && ` · ${verif.otros_tipos} NC/ND`}
              {verif.duplicados > 0 && (
                <span className="text-amber-400 font-bold"> · {verif.duplicados} duplicados</span>
              )}
              {verif.sin_factura?.length > 0 && ` · ${verif.sin_factura.length} sin comprobante`}
            </span>
          </div>

          {verif.duplicados > 0 && (
            <div className="bg-[#1A1D27] rounded-xl border border-amber-900/50 overflow-hidden">
              <button
                onClick={() => setDuplicadosAbiertos((v) => !v)}
                className="w-full px-4 py-3 border-b border-gray-800/50 bg-amber-950/20 flex items-center gap-2 hover:bg-amber-950/30 transition-colors text-left"
              >
                {duplicadosAbiertos
                  ? <ChevronDown className="w-3.5 h-3.5 text-amber-500" />
                  : <ChevronRight className="w-3.5 h-3.5 text-amber-500" />}
                <Copy className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">
                  Cargados más de una vez en YiQi ({verif.duplicados})
                </span>
                <span className="text-[11px] text-gray-500 ml-auto">
                  {duplicadosAbiertos ? 'ocultar' : 'ver detalle'}
                </span>
              </button>
              {duplicadosAbiertos && (
              <>
              <p className="px-4 pt-3 text-xs text-gray-400">
                El mismo número existe repetido. Para poder seguir se tomó el de importe más
                parecido al de ARCA, pero el importador de YiQi puede haber elegido el otro
                (de ahí salen "Diferente total" que en realidad son duplicados).
                Resolvelos en YiQi y volvé a conciliar.
              </p>
              <div className="p-4 space-y-3">
                {comprobantes.filter((c) => c.duplicado).map((c, i) => (
                  <div key={i} className="rounded-lg border border-gray-800 overflow-hidden">
                    <div className="px-3 py-2 bg-black/20 flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-white">{c.numero}</span>
                      <span className="text-[11px] text-gray-500">{c.tipo}</span>
                      <Badge tono={tonoResultado(c.resultado_arca)}>{c.resultado_arca}</Badge>
                      <span className="text-[11px] text-gray-500">
                        ARCA: {fmtNum(c.total_arca)}
                      </span>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {c.candidatos?.map((cand, j) => (
                          <tr key={j} className="border-t border-gray-800/50 text-gray-300">
                            <td className="px-3 py-1.5 w-8">
                              {cand.elegido && (
                                <span className="text-[10px] text-cyan-400 font-bold">usado</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-gray-500 font-mono">id {cand.id}</td>
                            <td className="px-3 py-1.5">
                              <Badge tono={tonoEstado(cand.esta_codigo, cand.estado)} sufijo={cand.esta_codigo}>
                                {cand.estado}
                              </Badge>
                            </td>
                            <td className="px-3 py-1.5">{cand.proveedor}</td>
                            <td className="px-3 py-1.5 text-gray-500">{cand.fecha}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtNum(cand.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              </>
              )}
            </div>
          )}

          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-4 space-y-3">
            <FiltroChecks
              titulo="Estado en YiQi"
              opciones={opcionesEstado}
              seleccion={filtroEstados}
              onToggle={alternar(filtroEstados, setFiltroEstados)}
              onTodos={() =>
                setFiltroEstados(
                  filtroEstados.length === opcionesEstado.length ? [] : opcionesEstado.map((o) => o.clave)
                )
              }
            />
            <FiltroChecks
              titulo="Resultado ARCA"
              opciones={opcionesResultado}
              seleccion={filtroResultados}
              onToggle={alternar(filtroResultados, setFiltroResultados)}
              onTodos={() =>
                setFiltroResultados(
                  filtroResultados.length === opcionesResultado.length ? [] : opcionesResultado.map((o) => o.clave)
                )
              }
            />
          </div>

          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-800/50 bg-black/20 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                Comprobantes por estado
              </span>
              <span className="text-[11px] text-gray-500">
                mostrando {comprobantesFiltrados.length} de {comprobantes.length}
              </span>
            </div>
            {comprobantesFiltrados.length === 0 ? (
              <p className="p-6 text-sm text-gray-500 text-center">
                Ningún comprobante coincide con el filtro.
              </p>
            ) : (
              <div className="max-h-[32rem] overflow-y-auto custom-scrollbar">
                <table className="w-full text-xs">
                  <thead className="bg-black/20 sticky top-0">
                    <tr className="text-gray-500">
                      <th className="text-left px-4 py-2 font-bold">Estado</th>
                      <th className="text-left px-4 py-2 font-bold">Tipo</th>
                      <th className="text-left px-4 py-2 font-bold">Proveedor</th>
                      <th className="text-left px-4 py-2 font-bold">Número</th>
                      <th className="text-left px-4 py-2 font-bold">Fecha</th>
                      <th className="text-right px-4 py-2 font-bold">Total YiQi</th>
                      <th className="text-right px-4 py-2 font-bold">Total ARCA</th>
                      <th className="text-left px-4 py-2 font-bold">Resultado ARCA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comprobantesFiltrados.map((f, i) => (
                      <tr key={i} className="border-t border-gray-800/50 text-gray-300">
                        <td className="px-4 py-2">
                          <Badge tono={tonoEstado(f.esta_codigo, f.estado)} sufijo={f.esta_codigo}>
                            {f.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-gray-500">{f.tipo || '—'}</td>
                        <td className="px-4 py-2">{f.proveedor}</td>
                        <td className="px-4 py-2 font-mono">
                          <span className="flex items-center gap-1.5">
                            {f.numero}
                            {f.duplicado && (
                              <Badge tono="amber">×{f.candidatos?.length} en YiQi</Badge>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">{f.fecha || '—'}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtNum(f.total_yiqi)}</td>
                        <td className={`px-4 py-2 text-right font-mono ${
                          f.total_yiqi != null && Math.abs((f.total_yiqi || 0) - (f.total_arca || 0)) > 0.01
                            ? 'text-amber-400' : ''
                        }`}>
                          {fmtNum(f.total_arca)}
                        </td>
                        <td className="px-4 py-2">
                          <Badge tono={tonoResultado(f.resultado_arca)}>{f.resultado_arca}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {verif.sin_factura?.length > 0 && (
            <p className="text-xs text-gray-500">
              {verif.sin_factura.length} comprobante(s) del período no aparecen en YiQi, ni como
              factura ni como nota de crédito.
            </p>
          )}
        </div>
      )}

      {/* ── Log ───────────────────────────────────────────────────────────── */}
      <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
        <div className="px-4 py-2 border-b border-gray-800/50 bg-black/20 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5 text-cyan-500" /> Trazabilidad del proceso
          </span>
          <div className="flex gap-1.5">
            <div className="w-2 h-2 rounded-full bg-red-500/50" />
            <div className="w-2 h-2 rounded-full bg-amber-500/50" />
            <div className="w-2 h-2 rounded-full bg-green-500/50" />
          </div>
        </div>
        <textarea
          ref={logRef}
          readOnly
          value={logs.length > 0 ? logs.join('\n') : placeholderLog}
          className={`w-full ${alturaLog} bg-[#0F1117] p-4 text-xs font-mono text-green-500 focus:outline-none resize-none overflow-y-auto custom-scrollbar`}
        />
      </div>

      {error && (
        <div className="bg-red-950/30 border border-red-900/50 rounded-xl p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-400">La conciliación falló</p>
            <p className="text-xs text-red-300/80 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* ── Comprobantes a revisar (lo que dijo ARCA) ──────────────────────── */}
      {resumen?.problemas?.length > 0 && (
        <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800/50 bg-black/20">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
              Comprobantes a revisar ({resumen.problemas.length})
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="bg-black/20 sticky top-0">
                <tr className="text-gray-500">
                  <th className="text-left px-4 py-2 font-bold">Proveedor</th>
                  <th className="text-left px-4 py-2 font-bold">Tipo</th>
                  <th className="text-left px-4 py-2 font-bold">Número</th>
                  <th className="text-right px-4 py-2 font-bold">Total ARCA</th>
                  <th className="text-left px-4 py-2 font-bold">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {resumen.problemas.map((p, i) => (
                  <tr key={i} className="border-t border-gray-800/50 text-gray-300">
                    <td className="px-4 py-2">{p.proveedor}</td>
                    <td className="px-4 py-2 text-gray-500">{p.tipo}</td>
                    <td className="px-4 py-2 font-mono">{p.numero}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtNum(p.total)}</td>
                    <td className="px-4 py-2">
                      <Badge tono={tonoResultado(p.resultado)}>{p.resultado}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Estado de una corrida ────────────────────────────────────────────────────
const ESTADO_CORRIDA = {
  ok: { texto: 'Importada', clase: 'text-emerald-400' },
  sin_comprobantes: { texto: 'Sin comprobantes', clase: 'text-gray-500' },
  error: { texto: 'Error', clase: 'text-red-400' },
};

// ── Fila del histórico, desplegable ──────────────────────────────────────────
const FilaHistorico = ({ corrida, abierta, detalle, cargando, onToggle }) => {
  const estado = ESTADO_CORRIDA[corrida.estado] || { texto: corrida.estado, clase: 'text-gray-400' };
  const resumen = corrida.yiqi?.resumen;
  const verif = corrida.yiqi?.verificacion;

  return (
    <>
      <tr
        onClick={onToggle}
        className="border-t border-gray-800/50 text-gray-300 hover:bg-white/[0.02] cursor-pointer"
      >
        <td className="pl-4 pr-1 py-2 w-6">
          {abierta
            ? <ChevronDown className="w-3.5 h-3.5 text-cyan-500" />
            : <ChevronRight className="w-3.5 h-3.5 text-gray-600" />}
        </td>
        <td className="px-4 py-2 text-gray-400">
          <span className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-gray-600" />
            {fmtFechaHora(corrida.inicio)}
          </span>
        </td>
        <td className="px-4 py-2">
          {corrida.desde || corrida.desde_pedido} → {corrida.hasta || corrida.hasta_pedido}
        </td>
        <td className="px-4 py-2 text-right font-mono">{corrida.comprobantes ?? '—'}</td>
        <td className="px-4 py-2"><span className={estado.clase}>{estado.texto}</span></td>
        <td className="px-4 py-2">
          {/* Vista previa: viaja en el listado liviano, sin el detalle pesado. */}
          <span className="flex gap-1.5 flex-wrap">
            {resumen && (
              <>
                <Badge tono="emerald">{resumen.ok} Ok</Badge>
                {resumen.creada > 0 && <Badge tono="sky">{resumen.creada} creadas</Badge>}
                <Badge tono="amber">{resumen.diferencia} dif.</Badge>
                <Badge tono="red">{resumen.no_existe} no existen</Badge>
              </>
            )}
            {verif?.no_pagadas > 0 && <Badge tono="amber">{verif.no_pagadas} sin pagar</Badge>}
          </span>
        </td>
        <td className="px-4 py-2 text-gray-500">{corrida.usuario}</td>
      </tr>

      {abierta && (
        <tr className="border-t border-gray-800/50">
          <td colSpan={7} className="bg-[#0F1117] p-5">
            {cargando ? (
              <div className="py-10 flex items-center justify-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando el detalle...
              </div>
            ) : !detalle ? (
              <p className="py-6 text-sm text-gray-500 text-center">
                No pude traer el detalle de esta corrida.
              </p>
            ) : (
              <DetalleConciliacion
                resultado={detalle.resultado}
                logs={detalle.logs || []}
                error={detalle.error}
                alturaLog="h-56"
                placeholderLog="Esta corrida es anterior al guardado del log, así que no quedó registrado."
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const ConciliacionArcaPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const BASE = `${API_URL}/api/admin/conciliacion-arca`;

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [corriendo, setCorriendo] = useState(false);
  const [logs, setLogs] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [cargandoHistorico, setCargandoHistorico] = useState(true);

  // Acordeón: una corrida abierta por vez (con 152 filas, dos abiertas no se leen).
  const [abierta, setAbierta] = useState(null);
  const [detalles, setDetalles] = useState({});      // cache por job_id
  const [cargandoDetalle, setCargandoDetalle] = useState(null);

  const pollRef = useRef(null);
  const cabeceras = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  const cargarHistorico = useCallback(async () => {
    setCargandoHistorico(true);
    try {
      const res = await fetch(`${BASE}/historico?limite=15`, { headers: cabeceras() });
      if (res.ok) {
        const data = await res.json();
        setHistorico(data.items || []);
      }
    } catch (e) {
      // el histórico es informativo: si falla, no bloquea la herramienta
      console.error('No pude traer el histórico', e);
    } finally {
      setCargandoHistorico(false);
    }
  }, [BASE, cabeceras]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/rango-sugerido`, { headers: cabeceras() });
        if (res.ok) {
          const data = await res.json();
          setDesde(aInput(data.desde));
          setHasta(aInput(data.hasta));
        }
      } catch (e) {
        console.error('No pude traer el rango sugerido', e);
      }
    })();
    cargarHistorico();
  }, [BASE, cabeceras, cargarHistorico]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── Desplegar una corrida del histórico ────────────────────────────────────
  const alternarFila = async (jobId) => {
    if (abierta === jobId) {
      setAbierta(null);
      return;
    }
    setAbierta(jobId);
    if (detalles[jobId]) return;                     // ya está en cache

    setCargandoDetalle(jobId);
    try {
      const res = await fetch(`${BASE}/historico/${jobId}`, { headers: cabeceras() });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setDetalles((prev) => ({ ...prev, [jobId]: data }));
    } catch (e) {
      console.error('No pude traer el detalle', e);
      toast.error('No pude traer el detalle de esa conciliación.');
    } finally {
      setCargandoDetalle(null);
    }
  };

  // ── Polling de la corrida en vivo ──────────────────────────────────────────
  const seguirJob = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BASE}/estado/${id}`, { headers: cabeceras() });
        if (!res.ok) return;
        const data = await res.json();
        setLogs(data.logs || []);

        if (data.estado !== 'corriendo') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setCorriendo(false);
          setResultado(data.resultado);
          setError(data.error);
          if (data.estado === 'ok') {
            const r = data.resultado?.yiqi?.resumen;
            toast.success(r
              ? `Conciliación lista: ${r.ok} Ok · ${r.diferencia} con diferencia · ${r.no_existe} no existen`
              : 'Conciliación terminada.');
          } else {
            toast.error(data.error || 'La conciliación falló.');
          }
          cargarHistorico();
        }
      } catch (e) {
        console.error('Error consultando el estado', e);
      }
    }, 2000);
  }, [BASE, cabeceras, cargarHistorico]);

  // ── Ejecutar ───────────────────────────────────────────────────────────────
  const ejecutar = async (forzar = false) => {
    setCorriendo(true);
    setLogs([]);
    setResultado(null);
    setError(null);

    try {
      const res = await fetch(`${BASE}/ejecutar`, {
        method: 'POST',
        headers: { ...cabeceras(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          desde: aBackend(desde) || null,
          hasta: aBackend(hasta) || null,
          forzar,
        }),
      });

      if (res.status === 409) {
        const d = await res.json();
        const detalle = d.detail;
        setCorriendo(false);
        if (detalle?.codigo === 'rango_ya_importado') {
          toast.warning(detalle.mensaje, {
            duration: 12000,
            action: { label: 'Importar igual', onClick: () => ejecutar(true) },
          });
        } else {
          toast.error(typeof detalle === 'string' ? detalle : 'Ya hay una conciliación en curso.');
        }
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === 'string' ? d.detail : `Error ${res.status}`);
      }

      const data = await res.json();
      setLogs([`[${new Date().toLocaleTimeString()}] Conciliación lanzada (${data.desde} al ${data.hasta}).`]);
      seguirJob(data.job_id);
    } catch (e) {
      setCorriendo(false);
      setError(e.message);
      toast.error(e.message);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* ── Encabezado ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-3">
            <FileSpreadsheet className="w-7 h-7 text-cyan-500" />
            Conciliación ARCA
          </h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Descarga Mis Comprobantes de ARCA para el período elegido y lo importa a YiQi.
            Las fechas que se mandan a YiQi son las que ARCA confirma haber aplicado.
          </p>
        </div>
        <button
          onClick={cargarHistorico}
          disabled={cargandoHistorico}
          className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${cargandoHistorico ? 'animate-spin' : ''}`} />
          Actualizar histórico
        </button>
      </div>

      {/* ── Controles ──────────────────────────────────────────────────────── */}
      <div className="bg-[#1A1D27] rounded-xl border border-gray-800 p-5">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-end justify-between">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">
                Desde
              </label>
              <input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                disabled={corriendo}
                className="bg-[#0F1117] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-600 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">
                Hasta
              </label>
              <input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                disabled={corriendo}
                className="bg-[#0F1117] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-600 disabled:opacity-50"
              />
            </div>
          </div>

          <button
            onClick={() => ejecutar(false)}
            disabled={corriendo || !desde || !hasta}
            className={`flex items-center gap-2 px-8 py-2.5 rounded-lg text-sm font-black transition-all shadow-lg ${
              corriendo
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white shadow-cyan-900/20 active:scale-95'
            }`}
          >
            {corriendo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
            {corriendo ? 'Conciliando...' : 'Ejecutar conciliación'}
          </button>
        </div>
      </div>

      {/* ── Corrida en vivo ────────────────────────────────────────────────── */}
      <DetalleConciliacion
        resultado={resultado}
        logs={logs}
        error={error}
        placeholderLog='Presioná "Ejecutar conciliación" para comenzar...'
      />

      {/* ── Histórico ──────────────────────────────────────────────────────── */}
      <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800/50 bg-black/20 flex items-center gap-2">
          <History className="w-3.5 h-3.5 text-cyan-500" />
          <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            Importaciones anteriores
          </span>
          <span className="text-[11px] text-gray-600">— tocá una fila para ver su detalle</span>
        </div>
        {cargandoHistorico ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
          </div>
        ) : historico.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Todavía no hay conciliaciones registradas.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-black/20">
              <tr className="text-gray-500">
                <th className="w-6" />
                <th className="text-left px-4 py-2 font-bold">Cuándo</th>
                <th className="text-left px-4 py-2 font-bold">Período</th>
                <th className="text-right px-4 py-2 font-bold">Comprobantes</th>
                <th className="text-left px-4 py-2 font-bold">Estado</th>
                <th className="text-left px-4 py-2 font-bold">Resultado</th>
                <th className="text-left px-4 py-2 font-bold">Usuario</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h) => (
                <FilaHistorico
                  key={h.job_id}
                  corrida={h}
                  abierta={abierta === h.job_id}
                  detalle={detalles[h.job_id]}
                  cargando={cargandoDetalle === h.job_id}
                  onToggle={() => alternarFila(h.job_id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ConciliacionArcaPage;
